import { FormEvent, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { toast } from "react-toastify";
import Input from "../ui/Input";
import LoadingButton from "../ui/LoadingButton";
import { PasswordCriteria } from "../PasswordCriteria";
import { useIntl } from "../../contexts/IntlContext";
import api from "../../services/api";
import { PasswordRule, PasswordValidator } from "../../utils/passwordValidator";

type Props = {
  open: boolean;
  onClose: () => void;
};

const INITIAL_FORM_ERRORS = { currentPassword: "", newPassword: "", confirmPassword: "" };

export function ChangePasswordDialog({ open, onClose }: Props) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordRules, setPasswordRules] = useState<PasswordRule[]>([]);
  const [formErrors, setFormErrors] = useState(INITIAL_FORM_ERRORS);

  const intl = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!open) return;

    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setFormErrors(INITIAL_FORM_ERRORS);
    setPasswordRules([]);
  }, [open]);

  useEffect(() => {
    if (newPassword || confirmPassword) {
      setPasswordRules(PasswordValidator.validateWithConfirm(newPassword, confirmPassword));
    }
  }, [newPassword, confirmPassword]);

  const validateForm = () => {
    let isValid = true;
    const errors = { ...INITIAL_FORM_ERRORS };

    if (!currentPassword) {
      errors.currentPassword = intl.formatMessage({ id: "resetPassword.passwordRequired" });
      isValid = false;
    }

    const passwordValidation = PasswordValidator.validateWithConfirm(newPassword, confirmPassword);
    const strengthFailed = passwordValidation.some((rule) => rule.name !== "confirm" && rule.error);
    const confirmFailed = passwordValidation.some((rule) => rule.name === "confirm" && rule.error);
    if (strengthFailed) {
      errors.newPassword = intl.formatMessage({ id: "validation.passwordCriteria" });
      isValid = false;
    }
    if (confirmFailed) {
      errors.confirmPassword = intl.formatMessage({ id: "validation.passwordMismatch" });
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await api.put("app/users/me/password", { currentPassword, newPassword });

      toast.success(intl.formatMessage({ id: "changePassword.success" }));
      onClose();
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: "changePassword.error" }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="change-password-dialog-title"
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle
        id="change-password-dialog-title"
        sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 1 }}
      >
        {intl.formatMessage({ id: "changePassword.title" })}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 0 }}>
        <Input
          label={intl.formatMessage({ id: "changePassword.currentPassword" })}
          type="password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          onBlur={() =>
            setFormErrors((prev) => ({
              ...prev,
              currentPassword: currentPassword
                ? ""
                : intl.formatMessage({ id: "resetPassword.passwordRequired" }),
            }))
          }
          error={formErrors.currentPassword !== ""}
          helperText={formErrors.currentPassword || intl.formatMessage({ id: "changePassword.currentPasswordPlaceholder" })}
        />

        <Input
          label={intl.formatMessage({ id: "resetPassword.newPassword" })}
          type="password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          error={formErrors.newPassword !== ""}
          helperText={formErrors.newPassword || intl.formatMessage({ id: "resetPassword.newPasswordPlaceholder" })}
        />

        <Input
          label={intl.formatMessage({ id: "resetPassword.confirmPassword" })}
          type="password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          error={formErrors.confirmPassword !== ""}
          helperText={formErrors.confirmPassword || intl.formatMessage({ id: "resetPassword.confirmPasswordPlaceholder" })}
        />

        {newPassword && <PasswordCriteria passwordRules={passwordRules} />}
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          pt: 1,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <LoadingButton onClick={onClose}>
          {intl.formatMessage({ id: "common.cancel" })}
        </LoadingButton>
        <LoadingButton loading={loading} onClick={handleSubmit} fullWidth={false}>
          {intl.formatMessage({ id: "changePassword.title" })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
